import { Group, NumberInput, TextInput } from '@mantine/core';

import type { RegistryOrderQuoteRequest } from '@/types/registry-documents';

/** The currency a quote is recorded in unless the desk said otherwise. */
export const DEFAULT_QUOTE_CURRENCY = 'GBP';

/** The desk's quote as it is being typed: the price and the turnaround
 * are what their inputs hold, a number or the empty string. */
export type QuoteDraft = {
  price: number | string;
  currency: string;
  turnaroundDays: number | string;
};

export const EMPTY_QUOTE: QuoteDraft = {
  price: '',
  currency: DEFAULT_QUOTE_CURRENCY,
  turnaroundDays: '',
};

/** The quote as the API takes it, or null while the draft is incomplete:
 * a price above zero, a three-letter currency, whole working days. */
export function quoteRequestFrom(draft: QuoteDraft): RegistryOrderQuoteRequest | null {
  const price = typeof draft.price === 'number' ? draft.price : Number(draft.price);
  const days = typeof draft.turnaroundDays === 'number' ? draft.turnaroundDays : NaN;
  const currency = draft.currency.trim().toUpperCase();
  if (!Number.isFinite(price) || price <= 0 || !/^[A-Z]{3}$/.test(currency)) {
    return null;
  }
  if (!Number.isInteger(days) || days < 1) {
    return null;
  }
  return { price: price.toFixed(2), currency, turnaround_days: days };
}

/**
 * The desk's quote: price, currency and turnaround in working days. One
 * row of inputs, shared by the reply dialog (which offers it behind a
 * checkbox) and the dialog that records a quote on its own.
 */
export function QuoteFields({
  draft,
  onChange,
}: Readonly<{
  draft: QuoteDraft;
  onChange: (draft: QuoteDraft) => void;
}>) {
  return (
    <Group grow align="flex-start">
      <NumberInput
        label="Price"
        placeholder="70.00"
        required
        min={0}
        decimalScale={2}
        fixedDecimalScale
        value={draft.price}
        onChange={(value) => onChange({ ...draft, price: value })}
      />
      <TextInput
        label="Currency"
        required
        maxLength={3}
        value={draft.currency}
        onChange={(e) => onChange({ ...draft, currency: e.currentTarget.value.toUpperCase() })}
      />
      <NumberInput
        label="Turnaround (working days)"
        placeholder="10"
        required
        min={1}
        allowDecimal={false}
        value={draft.turnaroundDays}
        onChange={(value) => onChange({ ...draft, turnaroundDays: value })}
      />
    </Group>
  );
}
